import ArgumentsCredentialsWrapper from '@/components/arguments/ArgumentsCredentialsWrapper';
import Button from '@/components/Button';
import Panel from '@/components/panel/Panel';
import { FiAlertOctagon, PiCheckCircleFill } from '@/components/react-icons/main';
import Skeleton from '@/components/Skeleton';
import Spinner from '@/components/Spinner';
import IconV2 from '@/components/v2/IconV2';
import Wizard from '@/components/wizard/Wizard';
import useBoundForm from '@/hooks/useBoundForm';
import openOauthWindow from '@/lib/openOauthWindow';
import sendToast from '@/lib/sendToast';
import { getCredentialById, getM2MOauthToken, testCredential } from '@/redux/actions/gravityActions';
import { openDocDrawer } from '@/redux/actions/uiActions/drawerActions';
import setPref from '@/redux/actions/uiActions/setPref';
import waitForSyncReady from '@/redux/actions/waitForSyncReady';
import { AnimatePresence, m } from 'framer-motion';
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { FiArrowUpRight } from 'react-icons/fi';
import CredentialWizardButtons from './CredentialWizardButtons';
import CredentialWizardHeader from './CredentialWizardHeader';
import AuthTypePopover from './AuthTypePopover';
import credentialActions from '@/redux/actions/credentialActions.js';
import { generateDeepDiffs } from '@shared/utils/generateDeepDiffs.js';
import { getAuthByCredential } from '@shared/utils/getAuthByCredential.js';
import { getOAuth2MachineToMachineFields } from '@shared/utils/getOAuth2MachineToMachineFields.js';
import { getStandardAuthFields } from '@shared/utils/getStandardAuthFields.js';

const getInitialState = (secrets) => ({ ...(secrets || {}) });

const CredentialWizardContent = forwardRef(({ app, companyId, credentialId, credential, initialSecrets, canEditCredentials, dataSource, loading, onClose }, ref) => {
  const [phase, setPhase] = useState('setup');
  const [isSaving, setIsSaving] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [isOAuth2Flow, setIsOAuth2Flow] = useState(false);
  const [isChangingAuthType, setIsChangingAuthType] = useState(false);
  const credentialsRef = useRef();
  const form = useBoundForm({ initialState: getInitialState(credential?.secrets) });
  const [isEdit, setIsEdit] = useState(false);

  // Normal auth
  const authObject = getAuthByCredential({ authentication: app?.authentication, credential });
  const baseAuthFields = getStandardAuthFields({ authType: authObject?.type, auth: authObject });
  const authFields = [
    ...baseAuthFields,
    ...getOAuth2MachineToMachineFields({
      auth: authObject,
      existingFields: [...baseAuthFields, ...(authObject?.arguments || [])]
    })
  ];
  const requiredArgs = [...authFields, ...(authObject?.arguments || [])].filter((n) => n.required);
  const hasTestAction = !!app?.credentialTestAction;
  const hasCustomAuthAction = !!authObject?.action?.actionId;
  const hasMoreThanOneAuthType = app?.authentication?.length > 1;

  // OAuth2 auth
  const isOAuth2 = credential?.meta?.type === 'oAuth2' || credential?.secrets?.type === 'oAuth2';
  const oAuth2Object = isOAuth2 ? getAuthByCredential({ authentication: app?.authentication, credential }) : {};
  const isM2M = isOAuth2 && oAuth2Object?.options?.enableM2M;
  const baseOAuth2Fields = getStandardAuthFields({ authType: oAuth2Object?.type, auth: oAuth2Object });
  const oAuth2Fields = isOAuth2
    ? [
        ...baseOAuth2Fields,
        ...getOAuth2MachineToMachineFields({
          auth: oAuth2Object,
          existingFields: [...baseOAuth2Fields, ...(oAuth2Object?.arguments || [])]
        })
      ]
    : [];
  const requiredOAuth2Fields = isOAuth2 ? [...oAuth2Fields, ...(oAuth2Object?.arguments || [])].filter((n) => n.required) : [];
  const isConnect = isOAuth2 && (!credential?.status || credential?.status === 'unknown' || credential?.status === 'incomplete');
  const isReconnect = isOAuth2 && !isConnect;

  const hasFilledRequiredArgs = useMemo(() => (requiredArgs?.length === 0 ? true : requiredArgs?.every((n) => Boolean(form.state?.[n.key]))), [JSON.stringify(requiredArgs), form.state]);
  const hasFilledRequiredOAuth2Args = useMemo(() => (requiredOAuth2Fields?.length === 0 ? true : requiredOAuth2Fields?.every((n) => Boolean(form.state?.[n.key]))), [JSON.stringify(requiredOAuth2Fields), form.state]);

  const hasAllRequiredFields = !isOAuth2 ? hasFilledRequiredArgs : hasFilledRequiredOAuth2Args;

  const buttonLabel = useMemo(() => {
    if (!hasAllRequiredFields) {
      return 'Save Draft';
    }
    if (!isOAuth2) {
      return hasTestAction ? 'Continue' : isEdit ? 'Reconnect' : 'Connect';
    }
    if (isSaving) {
      return 'Connecting...';
    }
    return isReconnect ? 'Reconnect' : 'Connect';
  }, [hasAllRequiredFields, isOAuth2, hasTestAction, isSaving, isReconnect, isEdit]);

  const isButtonDisabled = isSaving;

  const handleNext = (next) => {
    setPhase(next);
    if (next === 'setup') {
      setIsOAuth2Flow(false);
    }
  };

  const handleChange = ({ key, value }) => {
    form.set(key, value);
  };

  const handleOauth2 = async ({ credentialId, reconnect = false }) => {
    setIsOAuth2Flow(true);

    if (isM2M) {
      await getM2MOauthToken({ appSlug: app?.slug, draft: app?.version === 'draft', credentialId }, (data) => {
        if (data?.success) {
          sendToast({ message: 'You have successfully reconnected to the app.', type: 'success', duration: 4000 });
          setIsTesting(false);
          setTestResult({ success: true });
          handleNext('testing');
        } else {
          setIsTesting(false);
          setTestResult({ success: false });
          handleNext('testing');
        }
      });
      return;
    }

    if (!isM2M) {
      setPref('platformLoading', { isLoading: true, message: `Connecting to ${app?.name}...` });
      window.gravityHandleOAuth2Callback = (response) => {
        setPref('platformLoading', { isLoading: false });
        if (response?.success && response?.data?.credentialId) {
          getCredentialById(response?.data?.credentialId, { secrets: true, companyId, forceUpdate: true });
          setIsTesting(false);
          setTestResult({ success: true });
          handleNext('testing');
          return;
        }
        setIsTesting(false);
        setTestResult({ success: false });
        handleNext('testing');
      };

      const onClose = () => {
        getCredentialById(credentialId, { secrets: true, companyId, forceUpdate: true }, (res) => {
          const newCredential = res?.[0];
          if (newCredential?.status === 'valid') {
            sendToast({ message: `You have successfully signed in!`, type: 'success', duration: 4000 });
            setIsTesting(false);
            setTestResult({ success: true });
            handleNext('testing');
          } else {
            setIsTesting(false);
            setTestResult({ success: false });
            handleNext('testing');
          }
        });
      };

      if (reconnect || credentialId) {
        openOauthWindow({ appSlug: app?.slug, credentialId, companyId, draft: app?.version === 'draft', onClose });
      } else {
        setPref('platformLoading', { isLoading: false });
      }
    }
  };

  const handleSave = async () => {
    await waitForSyncReady();
    const diffs = generateDeepDiffs(credential?.secrets, form.state);
    if (diffs?.length > 0) {
      credentialsRef?.current?.onSave({ fullSecrets: form.state });
    }

    const hasOnlyLabelChange = diffs?.length === 1 && diffs?.[0]?.key === 'label';
    const hasAdvancedSettingsChange = diffs?.some((diff) => ['timezone', 'environmentId'].includes(diff?.key));
    if (hasOnlyLabelChange || hasAdvancedSettingsChange) {
      const message = hasOnlyLabelChange ? 'Connection label saved!' : 'Connection advanced settings saved!';
      sendToast({ message, type: 'success' });
    }
  };

  const handleConnect = async () => {
    return handleOauth2({ credentialId: credential?.credentialId, reconnect: isReconnect });
  };

  const handleSaveOrConnect = async () => {
    setIsSaving(true);
    if (isReconnect) {
      setIsReconnecting(true);
    }
    await handleSave();

    if (!hasAllRequiredFields) {
      setIsSaving(false);
      onClose();
      return;
    }

    if (isOAuth2) {
      await waitForSyncReady();
      setIsSaving(false);
      await handleConnect();
      setIsReconnecting(false);
      return;
    }

    setIsSaving(false);
    if (hasTestAction || hasCustomAuthAction) {
      handleNext('testing');
    } else {
      onClose();
    }
  };

  const handleSaveAndClose = async () => {
    await handleSave();
    onClose();
  };

  useImperativeHandle(ref, () => ({
    saveAndClose: handleSaveAndClose
  }));

  const handleHelp = () => {
    if (authObject?.helpDocumentUrl) {
      window.open(authObject?.helpDocumentUrl, '_blank');
      return;
    }
    openDocDrawer({ docId: authObject?.helpDocumentId });
  };

  useEffect(() => {
    form.setState(getInitialState(credential?.secrets));
  }, [JSON.stringify(credential?.secrets)]);

  useEffect(() => {
    if (phase !== 'testing') {
      return;
    }

    // Skip testing for OAuth2 flows - they set testResult directly
    if (isOAuth2Flow) {
      return;
    }

    const runTest = async () => {
      setIsTesting(true);
      await new Promise((resolve) => setTimeout(resolve, 1000));
      await waitForSyncReady();
      const data = await testCredential({ credentialId, appId: app?.id, appVersion: app?.version });
      const refreshed = await getCredentialById(credentialId, { secrets: true, companyId });
      setIsTesting(false);
      const result = data?.success !== undefined ? data : { success: refreshed?.[0]?.status === 'valid' };
      setTestResult(result);
    };

    setTimeout(runTest, 100);
  }, [phase, app?.id, app?.version, credentialId, companyId, isOAuth2Flow]);

  useEffect(() => {
    if (credential?.status) {
      setIsEdit(true);
    }
  }, []);

  useEffect(() => {
    if (credentialId) {
      setPhase('setup');
    }
  }, [credentialId]);

  useEffect(() => {
    if (!app?.authentication?.length || Object.keys(credential?.meta || {}).length === 0 || Object.keys(credential?.secrets || {}).length === 0) {
      return;
    }

    const credentialAuthId = credential?.meta?.authId || credential?.secrets?.authId;
    const credentialAuthType = credential?.meta?.type || credential?.secrets?.type;

    if (!credentialAuthId) {
      const authId = app?.authentication?.find((n) => n?.type === credentialAuthType)?.id;
      if (authId) {
        credentialActions.set({ ...credential, meta: { ...credential?.meta, authId }, secrets: { ...credential?.secrets, authId } });
      }
    }
  }, [app, credential]);

  const testingTitle = useMemo(() => {
    if (isTesting) {
      return 'Testing connection';
    }
    if (testResult?.success) {
      return "You're connected!";
    }
    return 'Connection failed';
  }, [isTesting, testResult?.success]);

  const testingDescription = useMemo(() => {
    if (isTesting) {
      return "We're verifying access and permissions.";
    }
    if (testResult?.success) {
      return 'This account is now available to use in workflows.';
    }
    if (!testResult?.success) {
      return "We couldn't connect to the app.";
    }
    return '';
  }, [isTesting, testResult?.success]);

  return (
    <div className="credential-wizard-content">
      <AnimatePresence mode="wait" initial={false}>
        {phase === 'setup' && (
          <m.div //
            key="setup-phase"
            initial={{ x: -200, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -200, opacity: 0 }}
            transition={{ type: 'spring', duration: 0.3, bounce: 0 }}>
            <Wizard>
              <CredentialWizardHeader icon={app?.icon} title={isEdit ? `Edit ${app?.name} Connection` : `Connect to ${app?.name}`} description={`Set up access so your workflows can talk to this app.`} loading={loading} />
              <Wizard.Body>
                {(loading || isChangingAuthType) && <Skeleton count={4} variant={'light'} skeletonStyle={{ height: '32px' }} />}
                {!loading && !isChangingAuthType && (
                  <>
                    {credential?.secretsWithheld === true && <Wizard.InfoBox className="credential-owner-notice">{credential?.viewerCanManage === true ? `Saved values are hidden — this connection is owned by ${credential?.connectionOwner?.name || 'another member'}. Enter new values to update it.` : `This connection is owned by ${credential?.connectionOwner?.name || 'another member'}. Only the connection owner or an organization Owner can change it.`}</Wizard.InfoBox>}
                    {(authObject?.helpDocumentId || authObject?.helpDocumentUrl) && (
                      <Button className="help-button full-width" size="xl" onClick={handleHelp} icon={FiArrowUpRight} iconPosition="right">
                        Learn how to set up this connection
                      </Button>
                    )}
                    <ArgumentsCredentialsWrapper //
                      ref={credentialsRef}
                      appId={app?.id}
                      appVersion={app?.version}
                      key={`${credential?.meta?.type || credential?.secrets?.type}-${credentialId}`}
                      formSecrets={form.state}
                      initialSecrets={initialSecrets}
                      dataSource={dataSource}
                      credentialId={credentialId}
                      disabled={!canEditCredentials}
                      onChange={handleChange}
                      credentialSlider
                      showLabel={false}
                    />
                    {hasMoreThanOneAuthType && <AuthTypePopover app={app} credential={credential} companyId={companyId} onTypeChangeStart={() => setIsChangingAuthType(true)} onTypeChangeEnd={() => setIsChangingAuthType(false)} />}
                  </>
                )}
              </Wizard.Body>
              <CredentialWizardButtons>
                {(isReconnect || isReconnecting || (isEdit && hasCustomAuthAction && !isOAuth2)) && (
                  <Button size="xl" className="full-width pill" onClick={() => (hasTestAction ? handleNext('testing') : handleSaveAndClose())} disabled={loading || isChangingAuthType}>
                    {hasTestAction ? 'Skip' : 'Close'}
                  </Button>
                )}
                <Button size="xl" className="full-width primary pill" onClick={handleSaveOrConnect} disabled={isButtonDisabled || loading || isChangingAuthType} loading={isSaving}>
                  {buttonLabel}
                </Button>
              </CredentialWizardButtons>
            </Wizard>
          </m.div>
        )}
        {phase === 'testing' && (
          <m.div //
            key="testing-phase"
            initial={{ x: 200, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 200, opacity: 0 }}
            transition={{ type: 'spring', duration: 0.3, bounce: 0 }}>
            <Wizard>
              <CredentialWizardHeader icon={app?.icon} title={testingTitle} description={testingDescription} loading={loading} />
              <Wizard.Body>
                {isTesting && (
                  <Panel.Section headerless className="credential-testing-status testing">
                    <div className="testing-content">
                      <Spinner variant="default" />
                      <p>Testing your connection...</p>
                    </div>
                  </Panel.Section>
                )}
                {!isTesting && testResult && (
                  <Panel.Section headerless className={`credential-testing-status ${testResult?.success ? 'success' : 'failure'}`}>
                    <div className="testing-content">
                      {testResult?.success ? (
                        <>
                          <IconV2 icon={PiCheckCircleFill} size={16} />
                          <p>Connection verified!</p>
                        </>
                      ) : (
                        <>
                          <IconV2 icon={FiAlertOctagon} size={14} />
                          <p>Invalid connection, please review the provided values.</p>
                        </>
                      )}
                    </div>
                  </Panel.Section>
                )}
              </Wizard.Body>
              {!isTesting && testResult && (
                <CredentialWizardButtons>
                  {testResult?.success ? (
                    <Button size="xl" className="full-width primary pill" onClick={onClose} disabled={loading}>
                      Close
                    </Button>
                  ) : (
                    <>
                      <Button size="xl" className="full-width pill" onClick={onClose} disabled={loading}>
                        Close
                      </Button>
                      <Button size="xl" className="full-width primary pill" onClick={() => handleNext('setup')} disabled={loading}>
                        Go back
                      </Button>
                    </>
                  )}
                </CredentialWizardButtons>
              )}
            </Wizard>
          </m.div>
        )}
      </AnimatePresence>
    </div>
  );
});

CredentialWizardContent.displayName = 'CredentialWizardContent';

export default CredentialWizardContent;
